/**
 * Dates and times — spec 01 §19: "Date/time theo locale và timezone".
 *
 * Two axes, and both are required rather than one:
 *
 *  - the **locale** decides the wording and the field order, and comes from the
 *    viewer's choice;
 *  - the **time zone** decides which instant is being named, and comes from the
 *    session (`SessionUser.timezone`, which the server fills from the employee row
 *    or the company default `WP_DEFAULT_TIMEZONE`).
 *
 * Rendering a company's attendance in the viewer's *browser* zone would be wrong in
 * the most expensive possible way: a manager travelling would see everyone's shift
 * shifted by hours and would open a correction queue about it. So the zone always
 * comes from the session and is passed explicitly — there is no default parameter
 * here that would let a caller forget it.
 */

const RELATIVE_UNITS: readonly (readonly [Intl.RelativeTimeFormatUnit, number])[] = [
  ['second', 1000],
  ['minute', 60_000],
  ['hour', 3_600_000],
  ['day', 86_400_000],
];

/**
 * An absolute timestamp in the company's zone, with the zone named.
 *
 * The zone abbreviation is part of the output on purpose: two people reading the
 * same screen from two countries must be able to see that they are looking at the
 * same wall clock.
 */
/**
 * Explicit components rather than `dateStyle`/`timeStyle`.
 *
 * Not a style preference: ECMA-402 makes `dateStyle`/`timeStyle` **mutually
 * exclusive** with every individual component option, and `timeZoneName` is one of
 * them — the pair throws `TypeError: Invalid option`. Since naming the zone is the
 * requirement (see the module comment), the shorthand is unavailable and the fields
 * are listed. `hour12: false` because a 24-hour clock is what a Vietnamese
 * administrative system uses and what a shift table is read in.
 */
const INSTANT_FIELDS = {
  year: 'numeric',
  month: 'short',
  day: '2-digit',
  hour: '2-digit',
  minute: '2-digit',
  hour12: false,
  timeZoneName: 'short',
} as const satisfies Intl.DateTimeFormatOptions;

export function formatInstant(iso: string, locale: string, timeZone: string): string {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return iso;
  try {
    return new Intl.DateTimeFormat(locale, { ...INSTANT_FIELDS, timeZone }).format(date);
  } catch {
    // An unknown IANA zone is a bad row upstream, not a reason to render nothing.
    // UTC, explicitly labelled, is the answer that cannot be misread as local time.
    return new Intl.DateTimeFormat(locale, { ...INSTANT_FIELDS, timeZone: 'UTC' }).format(date);
  }
}

/**
 * "3 minutes ago" — how stale, in the viewer's language.
 *
 * Used **alongside** the absolute time, never instead of it. "2 hours ago" is what a
 * person reads to decide whether to act; the timestamp is what they quote when they
 * report it, and a relative age alone cannot be quoted.
 */
export function formatAge(iso: string, locale: string, now: Date = new Date()): string {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return iso;
  const deltaMs = date.getTime() - now.getTime();
  const magnitude = Math.abs(deltaMs);

  let unit: Intl.RelativeTimeFormatUnit = 'second';
  let divisor = 1000;
  for (const [candidateUnit, candidateDivisor] of RELATIVE_UNITS) {
    if (magnitude >= candidateDivisor) {
      unit = candidateUnit;
      divisor = candidateDivisor;
    }
  }

  const formatter = new Intl.RelativeTimeFormat(locale, { numeric: 'auto' });
  return formatter.format(Math.round(deltaMs / divisor), unit);
}

/**
 * The pair a state component shows: "14 thg 5, 2026 09:12 GMT+7 (2 giờ trước)".
 *
 * `null` is a value, not a blank — `PRODUCT.md` §4 exists because an absence
 * rendered as nothing is indistinguishable from a zero. Callers pass the "never"
 * wording so it can be phrased for the thing that never happened.
 */
export function formatInstantWithAge(
  iso: string | null,
  locale: string,
  timeZone: string,
  neverLabel: string,
  now?: Date,
): string {
  if (iso === null) return neverLabel;
  return `${formatInstant(iso, locale, timeZone)} (${formatAge(iso, locale, now)})`;
}

/* ══════════ Task 42 (WP-K2) — the other direction: a wall clock to an instant ══
 *
 * Everything above turns an instant into words. Manual time entry needs the reverse,
 * and it is the harder direction: `<input type="datetime-local">` yields a **wall
 * clock with no zone** — `2026-08-24T09:00` — and `new Date(...)` on that string
 * resolves it in the *browser's* zone.
 *
 * That would be wrong in exactly the way the module comment above warns about. A
 * manager filing from a different country would send an instant hours away from the
 * one they typed, and the entry would land in somebody's day at the wrong time. The
 * zone that decides which instant `09:00` names is the **company's**, from
 * `SessionUser.timezone`, exactly as it is for every rendered timestamp.
 *
 * ── How, with no dependency ────────────────────────────────────────────────
 *
 * The same two-step solve `src/lib/timezone.ts` uses on the server, and the same
 * reason for it: `Intl` can tell you a zone's offset **at an instant**, but the
 * question here is the offset at a *local time*, which is not the same question and
 * has no direct API. So: guess the instant by reading the wall clock as UTC, ask
 * what the zone's offset is there, correct, and ask again. Two iterations converge
 * for every zone whose offsets differ by less than a day, which is all of them.
 *
 * The pathological cases are named rather than hidden. In a spring-forward **gap**
 * the typed time does not exist and the solve lands on the instant just after the
 * jump; in an autumn **overlap** it names the first of the two. Both are one hour,
 * twice a year, and both are resolved deterministically. The form renders the result
 * back in the company zone before it is submitted, so a person can see which instant
 * they are about to claim rather than trusting this arithmetic — which is the real
 * mitigation.
 */

/** Milliseconds `zone` is ahead of UTC at `instantMs`. */
export function zoneOffsetMsAt(zone: string, instantMs: number): number {
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone: zone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false,
  }).formatToParts(new Date(instantMs));
  const field = (type: Intl.DateTimeFormatPartTypes): number =>
    Number(parts.find((part) => part.type === type)?.value ?? '0');
  // `hour12: false` renders midnight as `24` in some ICU versions; `% 24` is the
  // documented normalisation and is a no-op for every other hour.
  const asUtc = Date.UTC(
    field('year'),
    field('month') - 1,
    field('day'),
    field('hour') % 24,
    field('minute'),
    field('second'),
  );
  return asUtc - instantMs;
}

/**
 * `2026-08-24T09:00` in `zone` → the RFC3339 instant the API is sent.
 *
 * Returns `null` for anything that is not a wall clock this function can solve,
 * because a manual entry built on a silently defaulted instant is worse than a form
 * that refuses to submit.
 */
export function wallClockToInstant(wall: string, zone: string): string | null {
  const match = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})(?::(\d{2}))?$/.exec(wall);
  if (match === null) return null;
  const [, year, month, day, hour, minute, second] = match;
  const asUtc = Date.UTC(
    Number(year),
    Number(month) - 1,
    Number(day),
    Number(hour),
    Number(minute),
    Number(second ?? '0'),
  );
  if (Number.isNaN(asUtc)) return null;
  let instant = asUtc - zoneOffsetMsAt(zone, asUtc);
  instant = asUtc - zoneOffsetMsAt(zone, instant);
  return new Date(instant).toISOString();
}

/**
 * The local date `instantMs` falls on in `zone`, as `YYYY-MM-DD`.
 *
 * `toISOString().slice(0, 10)` is the version of this that looks right and is wrong
 * by a day for a third of the world for part of every day: it is UTC's date, not the
 * company's. Attendance, aggregates and corrections are all keyed on the company's
 * local date, so "which day am I looking at" has to be answered in that zone.
 */
export function localDateIn(zone: string, instantMs: number): string {
  const shifted = instantMs + zoneOffsetMsAt(zone, instantMs);
  return new Date(shifted).toISOString().slice(0, 10);
}

/** `YYYY-MM-DD` plus `days`, calendar arithmetic, no zone involved. */
export function addDays(date: string, days: number): string {
  const ms = Date.parse(`${date}T00:00:00Z`);
  if (Number.isNaN(ms)) return date;
  return new Date(ms + days * 86_400_000).toISOString().slice(0, 10);
}

/**
 * A `YYYY-MM-DD` local calendar date, in words — "one date voice", spec 01 §19.
 *
 * Formatted from the digits in UTC on purpose: the string is already a local
 * calendar day resolved by `localDateIn` elsewhere, and re-projecting it through a
 * zone here would shift it back over a midnight boundary. This is the formatter
 * `/overview` prints every date through; every other screen that prints a date as
 * text goes through it too, so nobody reads an ISO string on one screen and a
 * Vietnamese sentence on the next.
 */
export function formatDay(
  date: string,
  locale: string,
  options: Intl.DateTimeFormatOptions,
): string {
  return new Intl.DateTimeFormat(locale, { ...options, timeZone: 'UTC' }).format(
    new Date(`${date}T00:00:00Z`),
  );
}

/**
 * A `YYYY-MM` month, in words — "Tháng 9, 2026" / "September 2026".
 *
 * Not `Intl`'s combined `{month: 'long', year: 'numeric'}`: for `vi` that inserts
 * "năm" ("tháng 9 năm 2026"), which is correct prose but not the compact heading
 * voice this product's month pickers and calendar titles use elsewhere (the day
 * form above reads "14 tháng 9, 2026", comma before the year, no "năm"). Vietnamese
 * names a month by its number rather than a word, so there is no month name to ask
 * `Intl` for either — the number is taken straight off the `YYYY-MM` string.
 */
export function formatMonth(month: string, locale: string): string {
  const match = /^(\d{4})-(\d{2})$/.exec(month);
  if (match === null) return month;
  const [, year, monthDigits] = match;
  if (locale === 'en') {
    return new Intl.DateTimeFormat('en', {
      month: 'long',
      year: 'numeric',
      timeZone: 'UTC',
    }).format(new Date(`${year}-${monthDigits}-01T00:00:00Z`));
  }
  return `Tháng ${Number(monthDigits)}, ${year}`;
}

/** The instant a local date begins in `zone`, RFC3339. */
export function startOfLocalDay(zone: string, date: string): string {
  return wallClockToInstant(`${date}T00:00`, zone) ?? `${date}T00:00:00.000Z`;
}

/**
 * `Asia/Ho_Chi_Minh` is an IANA identifier, not a name a person uses for their own
 * clock. The common zones of this suite's customers get the name and the offset they
 * are actually called by.
 *
 * It lives here, beside the formatters, rather than in `company/regions.ts` where
 * CS-2 first wrote it: `/attendance` printed the raw identifier in its sub-header
 * and a screen that is not about company settings should not have to import from
 * them to say what clock it is on (finish review, round 2, fix 4).
 */
const ZONE_NAME: Readonly<Record<string, { readonly vi: string; readonly en: string }>> = {
  'Asia/Ho_Chi_Minh': { vi: 'Giờ Việt Nam (UTC+7)', en: 'Vietnam time (UTC+7)' },
  'Asia/Bangkok': { vi: 'Giờ Thái Lan (UTC+7)', en: 'Thailand time (UTC+7)' },
  'Asia/Jakarta': { vi: 'Giờ Tây Indonesia (UTC+7)', en: 'Western Indonesia time (UTC+7)' },
  'Asia/Singapore': { vi: 'Giờ Singapore (UTC+8)', en: 'Singapore time (UTC+8)' },
  'Asia/Kuala_Lumpur': { vi: 'Giờ Malaysia (UTC+8)', en: 'Malaysia time (UTC+8)' },
  'Asia/Manila': { vi: 'Giờ Philippines (UTC+8)', en: 'Philippines time (UTC+8)' },
  'Asia/Tokyo': { vi: 'Giờ Nhật Bản (UTC+9)', en: 'Japan time (UTC+9)' },
  'Asia/Seoul': { vi: 'Giờ Hàn Quốc (UTC+9)', en: 'Korea time (UTC+9)' },
  'Australia/Sydney': { vi: 'Giờ miền đông Úc', en: 'Eastern Australia time' },
  UTC: { vi: 'Giờ UTC', en: 'UTC' },
};

/**
 * The offset a zone is on right now, as `UTC+7` / `UTC-3:30`.
 *
 * Computed rather than tabled, so an unlisted zone still gets a true offset instead
 * of an invented one. An identifier `Intl` refuses is rendered without an offset at
 * all — a wrong clock is worse than no clock.
 */
function utcOffsetLabel(zone: string): string | null {
  let minutes: number;
  try {
    minutes = Math.round(zoneOffsetMsAt(zone, Date.now()) / 60_000);
  } catch {
    return null;
  }
  const hours = Math.floor(Math.abs(minutes) / 60);
  const rest = Math.abs(minutes) % 60;
  const sign = minutes < 0 ? '-' : '+';
  return `UTC${sign}${String(hours)}${rest === 0 ? '' : `:${String(rest).padStart(2, '0')}`}`;
}

/** The clock, named the way the people reading it name it — never the identifier. */
export function timezoneName(zone: string, locale: string): string {
  const named = ZONE_NAME[zone];
  if (named !== undefined) return locale === 'en' ? named.en : named.vi;
  const opened = zone.replace(/_/g, ' ');
  const offset = utcOffsetLabel(zone);
  return offset === null ? opened : `${opened} (${offset})`;
}
